import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import cn from "classnames";

import useWindowDimensions from "@/next-src/hooks/useWindowDimensions";
import ButtonLink from "@/next-src/components/ui/button-link/ButtonLink";
import ResponsiveImage from "@/next-src/components/ui/responsive-image/ResponsiveImage";
import convertHTMLtoJSX from "@/next-src/utils/convertHTMLtoJSX";
import Calendly from "@/next-src/components/ui/calendly/Calendly";

import comCSS from "./Card.module.scss";

function convertRemToPixels(rem) {
  if (typeof window !== "object") return null;
  return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
}

function getCoords(elem) {
  let box = elem.getBoundingClientRect();

  return {
    top: box.top + window.scrollY,
    right: box.right + window.scrollX,
    bottom: box.bottom + window.scrollY,
    left: box.left + window.scrollX,
  };
}

const scaleRange = 0.05;

const Card = ({ className = "", data, isLast = false }) => {
  const { width: windowWidth } = useWindowDimensions();
  const [scaleIndex, setScaleIndex] = useState(1);
  const cardRef = useRef();

  useEffect(() => {
    if (typeof window !== "undefined") {
      const min = convertRemToPixels(8);
      const range = convertRemToPixels(6);
      const changeCounter = () => {
        if (isLast || !cardRef?.current || window.innerWidth < 992) return;
        const boundaryTop = window.scrollY + min;
        const boundaryBottom = window.scrollY + min + range;
        const elemTopPosition =
          cardRef.current?.offsetTop +
          getCoords(cardRef.current?.offsetParent).top;
        if (
          boundaryBottom >= elemTopPosition &&
          boundaryTop <= elemTopPosition
        ) {
          const scaleI =
            Math.round(
              (1 - ((boundaryBottom - elemTopPosition) / range) * scaleRange) *
                1000
            ) / 1000;
          setScaleIndex(scaleI);
        } else if (boundaryBottom < elemTopPosition) {
          setScaleIndex(1);
        }
      };

      const onScroll = () => {
        if (!cardRef?.current) return;
        changeCounter();
      };

      window.addEventListener("scroll", onScroll, false);
      window.addEventListener("resize", onScroll, false);

      // Call handler right away so state gets updated with initial window size
      onScroll();

      return () => {
        window.removeEventListener("scroll", onScroll, false);
        window.removeEventListener("resize", onScroll, false);
      };
    }
  }, []);

  const getText = (el, keyLG, keyXS) => {
    if (windowWidth < 992) {
      return convertHTMLtoJSX(el?.[keyXS]);
    }
    return convertHTMLtoJSX(el?.[keyLG]);
  };
  const getButton = (el, keyLG, keyXS) => {
    if (windowWidth < 992) {
      return el?.[keyXS];
    }
    return el?.[keyLG];
  };
  return (
    <div
      ref={cardRef}
      className={cn(
        className,
        comCSS.wrapper,
        "basic-card sticky-min-height-set"
      )}
      style={{
        willChange: "transform",
        transform: `translate3d(0px, 0px, 0px) scale3d(${scaleIndex}, ${scaleIndex}, 1) rotateX(0deg) rotateY(0deg) rotateZ(0deg) skew(0deg, 0deg)`,
        transformStyle: "preserve-3d",
      }}
    >
      <div
        className="program-card-text-wrap"
        style={{
          willChange: "opacity",
          opacity: scaleIndex < 1 ? 0.5 + scaleIndex * 0.1 : 1,
        }}
      >
        <div>
          <div className="margin-bottom margin-xsmall">
            {getText(data, "title", "titleMobile")}
          </div>
          <div className="margin-bottom margin-large">
            <div className="max-width-small text-color-slate-grey">
              {getText(data, "text", "textMobile")}
            </div>
          </div>
        </div>
        <div className="program-card-bottom-wrap">
          <div className="program-btns-wrap">
            <ButtonLink
              data={getButton(data, "button", "buttonMobile")}
              className="is-secondary"
            />

            {getButton(data, "secondButton", "secondButtonMobile")
              ?.calendlyLink ? (
              <Calendly
                classNameBtn="is-icon is-inverted"
                classNameBtnSuccess="is-icon is-inverted"
                data={{
                  calendlyButton: data.secondButton,
                  calendlyButtonMobile: data.secondButtonMobile,
                  calendlyButtonSuccess: data.calendlyButtonSuccess,
                  calendlyButtonSuccessMobile: data.calendlyButtonSuccessMobile,
                }}
                isPulsingIcon
              />
            ) : (
              <ButtonLink
                data={getButton(data, "secondButton", "secondButtonMobile")}
                className="is-icon is-inverted"
                isPulsingIcon
              />
            )}
          </div>
        </div>
      </div>
      <div
        className={cn(comCSS.imgWrapper, "program-img-wrap")}
        style={{
          willChange: "opacity",
          opacity: scaleIndex < 1 ? 0.5 + scaleIndex * 0.1 : 1,
        }}
      >
        <ResponsiveImage
          src={data.image?.src}
          alt={data.image?.alt}
          sizes="(max-width: 767px) 90vw, (max-width: 991px) 38vw, 35vw"
          lazyLoading
          width={405}
          height={384}
          className="cover-image"
        />
      </div>
      <div className="card-bg-color-white-blur" />
    </div>
  );
};

Card.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    titleMobile: PropTypes.string,
    text: PropTypes.string,
    textMobile: PropTypes.string,
    image: PropTypes.shape({
      id: PropTypes.string,
      src: PropTypes.string,
      alt: PropTypes.string,
    }),
    button: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    buttonMobile: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    secondButton: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    secondButtonMobile: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    calendlyButtonSuccess: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    calendlyButtonSuccessMobile: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
  }),
  isLast: PropTypes.bool,
};

export default Card;
